/**
 * Copiloto de IA da conversa (coluna da direita, Versão B do protótipo): estado da assistente
 * (desligada / sombra / sugestão / piloto), o rascunho da vez com o que o porteiro decidiu e as
 * fontes (ferramentas) usadas, e os controles Assumir / Devolver à IA.
 *
 *  - Sugestão: o rascunho fica pendente; o atendente edita e envia, ou descarta.
 *  - Sombra: nada é enviado; aparece o rascunho interno e, quando o humano responde, a parecença.
 *  - Piloto: a IA responde sozinha só em conversa com a tag (ou número liberado); aqui se vê o que ela enviou.
 *  - Se o servidor de IA não está publicado neste ambiente, o painel diz isso e some o resto.
 */
import React, { useState, type ReactElement } from "react";
import { Bot } from "lucide-react";
import type { AiDraft } from "../../api/ai-types.js";
import type { PlatformConversation } from "../../api/types.js";
import { useAiDraftAction, useAiDrafts, useAiStatus, useChannelConversationActions } from "../../queries.js";
import { Badge, Button, controlClass } from "../../ui/index.js";

const MODE_LABEL = { shadow: "Sombra", suggest: "Sugestão", pilot: "Piloto" } as const;
const MODE_HINT = {
  shadow: "A IA só aprende e gera rascunho interno. Nada é enviado ao cliente.",
  suggest: "A IA sugere a resposta e você aprova com um clique.",
  pilot: "A IA responde sozinha só nas conversas com a tag piloto."
} as const;
const INTENT_LABEL: Record<string, string> = { preco: "Preço", ficha_tecnica: "Ficha técnica", frete: "Frete", status_pedido: "Status do pedido", saudacao: "Saudação", reclamacao: "Reclamação", pedido_humano: "Pede atendente", outro: "Outro" };
export const REASON_LABEL: Record<string, string> = {
  customer_asked_human: "o cliente pediu um atendente", low_confidence: "confiança baixa do porteiro", not_answerable: "não dá para responder só com dados", urgent: "urgência alta",
  frustrated: "cliente irritado", intent_not_allowed: "assunto que exige pessoa", no_data: "sem dado nas ferramentas", validator_blocked: "o validador barrou valor/prazo sem fonte", model_failed: "falha do modelo"
};
const pct = (value: number | undefined) => `${Math.round((value ?? 0) * 100)}%`;

export function Gate({ draft }: { draft: AiDraft }): ReactElement | null {
  const gate = draft.gatekeeper;
  if (!gate) return null;
  const probabilities = Object.entries(gate.probabilities ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 3);
  return (
    <div className="flex flex-col gap-1 rounded-control bg-info-soft p-3 text-xs" data-section="gatekeeper">
      <b className="text-ink">{`Porteiro${gate.provider === "typesafe" ? " (Jev)" : gate.provider === "llm" ? " (modelo rápido)" : " (regras)"}${gate.latencyMs ? ` · ${gate.latencyMs} ms` : ""}`}</b>
      {probabilities.map(([intent, value]) => (
        <span key={intent} className="grid grid-cols-[6.5rem_1fr_2.5rem] items-center gap-2">
          <span className="text-muted">{INTENT_LABEL[intent] ?? intent}</span>
          <span className="h-1.5 rounded-pill bg-surface"><span className="block h-full rounded-pill bg-brand" style={{ width: pct(value) }} /></span>
          <b className="text-right text-ink">{pct(value)}</b>
        </span>
      ))}
      <span className="mt-1 flex flex-wrap gap-1">
        <Badge tone="success">{`pode responder ${pct(gate.canAnswer)}`}</Badge>
        <Badge tone="neutral">{`urgência ${pct(gate.urgency)}`}</Badge>
        <Badge tone="neutral">{`frustração ${pct(gate.frustration)}`}</Badge>
      </span>
    </div>
  );
}

function DraftCard({ draft, conversationId }: { draft: AiDraft; conversationId: string }): ReactElement {
  const act = useAiDraftAction(conversationId);
  const [text, setText] = useState(draft.text ?? "");
  const pending = draft.status === "pending" && draft.text;
  const reason = draft.reason ? (REASON_LABEL[draft.reason.split(":")[0] ?? ""] ?? draft.reason) : null;
  return (
    <article className="flex flex-col gap-2" data-draft-id={draft.id} data-draft-status={draft.status}>
      <Gate draft={draft} />
      {draft.action === "handoff" ? (
        <p className="m-0 rounded-control bg-warning-soft p-3 text-sm text-ink" data-draft-handoff="true">{`Foi para atendimento humano${reason ? `: ${reason}` : ""}.`}</p>
      ) : null}
      {draft.text ? (
        pending ? (
          <>
            <label className="text-xs font-black uppercase tracking-wider text-muted" htmlFor={`draft-${draft.id}`}>Rascunho da IA</label>
            <textarea id={`draft-${draft.id}`} className={controlClass + " min-h-24"} value={text} onChange={(event) => setText(event.target.value)} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => act.mutate({ id: draft.id, action: "approve", text })} disabled={act.isPending || !text.trim()}>Enviar</Button>
              <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: draft.id, action: "discard" })} disabled={act.isPending}>Descartar</Button>
            </div>
            {act.isError ? <p role="alert" className="m-0 text-xs font-bold text-danger">{`Não foi possível enviar: ${act.error.message.split(":")[0]}`}</p> : null}
          </>
        ) : (
          <div className="rounded-control border border-dashed border-border p-3 text-sm text-ink">
            <span className="mb-1 block text-xs font-black uppercase tracking-wider text-muted">
              {draft.status === "sent" ? "Enviado pela IA" : draft.status === "observed" ? "Rascunho interno (sombra)" : draft.status === "discarded" ? "Descartado" : "Rascunho"}
            </span>
            {draft.text}
            {draft.humanReply ? <span className="mt-2 block text-xs text-muted">{`Você respondeu: “${draft.humanReply}” · parecença ${pct(draft.agreement ?? 0)}`}</span> : null}
          </div>
        )
      ) : null}
      {draft.facts.length ? (
        <p className="m-0 flex flex-wrap gap-1" aria-label="Fontes usadas">
          {draft.facts.map((fact, index) => <Badge key={`${fact.tool}-${index}`} tone={fact.ok ? "info" : "warning"}>{`${fact.ok ? "fonte" : "sem dado"}: ${fact.tool}`}</Badge>)}
        </p>
      ) : null}
    </article>
  );
}

export function CopilotPanel({ conversation }: { conversation: PlatformConversation }): ReactElement | null {
  const status = useAiStatus();
  const drafts = useAiDrafts(conversation.id);
  const { setMode } = useChannelConversationActions(conversation.id);
  if (conversation.channel !== "whatsapp") return null;
  const human = conversation.mode === "human";
  const latest = (drafts.data ?? []).slice(0, 3);
  return (
    <section aria-label="Copiloto de IA" className="flex flex-col gap-3" data-panel="copilot">
      <h4 className="m-0 flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-muted"><Bot className="h-4 w-4" aria-hidden focusable={false} />Copiloto de IA</h4>
      {status.isError ? (
        <p className="m-0 rounded-control bg-surface-soft p-3 text-sm text-muted" data-copilot="unavailable">O assistente de IA ainda não está publicado neste ambiente.</p>
      ) : !status.data ? (
        <p className="m-0 text-sm text-muted">Carregando…</p>
      ) : !status.data.enabled ? (
        <p className="m-0 rounded-control bg-surface-soft p-3 text-sm text-muted" data-copilot="off">A IA está desligada. Quem administra a IA ativa em Configurações › IA.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={status.data.mode === "pilot" ? "success" : "info"}>{`Modo ${MODE_LABEL[status.data.mode]}`}</Badge>
            {status.data.learning.started ? <Badge tone="neutral">{`Aprendizado: dia ${status.data.learning.day} de ${status.data.learning.days}`}</Badge> : null}
          </div>
          <p className="m-0 text-xs text-muted">{MODE_HINT[status.data.mode]}</p>
          {latest.length === 0 ? <p className="m-0 text-sm text-muted">Nenhuma decisão da IA nesta conversa ainda.</p> : <DraftCard draft={latest[0]!} conversationId={conversation.id} />}
        </>
      )}
      {/* Assumir/devolver só faz sentido quando a IA pode responder sozinha (piloto). Desligada, sombra ou sugestão: atendimento 100% humano. */}
      {status.data?.enabled && status.data.mode === "pilot" ? (
        <>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={human ? "outline" : "primary"} onClick={() => setMode.mutate("takeover")} disabled={human || setMode.isPending}>Assumir conversa</Button>
            <Button size="sm" variant="outline" onClick={() => setMode.mutate("resume")} disabled={!human || setMode.isPending}>Devolver à IA</Button>
          </div>
          <p className="m-0 text-xs text-muted">Se você responder (aqui ou pelo celular), a IA pausa nesta conversa pelo tempo configurado.</p>
        </>
      ) : null}
    </section>
  );
}
